// src/pages/HomePage.jsx
import React, { useState } from "react";

const HomePage = () => {
  // State for the generated game idea
  const [gameIdea, setGameIdea] = useState("");
  // State for loading status
  const [isLoadingIdea, setIsLoadingIdea] = useState(false);

  // Function to call Gemini API and generate a game idea
  const generateGameIdea = async () => {
    setIsLoadingIdea(true);
    setGameIdea(""); // Clear previous idea

    const prompt =
      "Generate a unique and fun mobile game idea. Include its genre, a core gameplay mechanic, and a brief, exciting premise. Make it suitable for Android, iOS, and Amazon devices.";
    let chatHistory = [];
    chatHistory.push({ role: "user", parts: [{ text: prompt }] });

    const payload = { contents: chatHistory };
    const apiKey = ""; // Canvas will automatically provide the API key at runtime
    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (
        result.candidates &&
        result.candidates.length > 0 &&
        result.candidates[0].content &&
        result.candidates[0].content.parts &&
        result.candidates[0].content.parts.length > 0
      ) {
        const text = result.candidates[0].content.parts[0].text;
        setGameIdea(text);
      } else {
        setGameIdea("Failed to generate idea. Please try again.");
        console.error("Gemini API response structure unexpected:", result);
      }
    } catch (error) {
      setGameIdea("Error generating idea. Please check your connection.");
      console.error("Error calling Gemini API:", error);
    } finally {
      setIsLoadingIdea(false);
    }
  };

  return (
    <div className="py-12 md:py-20 bg-gray-100">
      <div className="container mx-auto px-4">
        {/* Hero Section - More dynamic background */}
        <section className="hero-section-gradient text-white p-8 md:p-20 rounded-3xl shadow-2xl flex flex-col items-center text-center relative overflow-hidden transform transition-all duration-500 ease-in-out hover:scale-[1.01]">
          {/* Enhanced background overlay for visual interest */}
          <div
            className="absolute inset-0 opacity-15"
            style={{
              backgroundImage:
                "url('https://placehold.co/1200x800/ffffff/00BCD4?text=Abstract+Game+Art')",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              filter: "blur(3px)", // Subtle blur for background
            }}
          ></div>
          <div className="relative z-10 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight font-inter drop-shadow-2xl">
              Unlock Endless Fun with{" "}
              <span className="text-amber-300">Insperity Games!</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mb-10 font-light drop-shadow-md">
              Dive into a vibrant world of adventure, strategy, and casual
              enjoyment with our diverse portfolio of high-quality titles for
              Android, iOS, and Amazon.
            </p>
            <div className="flex flex-col sm:flex-row space-y-5 sm:space-y-0 sm:space-x-6">
              <a
                href="https://play.google.com/store/apps/details?id=com.insperitygames.bit.coin"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-8 rounded-full shadow-xl transition duration-300 transform hover:scale-105 flex items-center justify-center space-x-3 text-lg animate-bounce-once"
              >
                <i className="fab fa-google-play text-2xl"></i>
                <span>Play on Google Play</span>
              </a>
              <a
                href="https://www.apple.com/ios/app-store/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-8 rounded-full shadow-xl transition duration-300 transform hover:scale-105 flex items-center justify-center space-x-3 text-lg animate-bounce-once delay-100"
              >
                <i className="fab fa-apple text-2xl"></i>
                <span>Download on the App Store</span>
              </a>
              <a
                href="https://www.amazon.com/gp/mas/get/android"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-8 rounded-full shadow-xl transition duration-300 transform hover:scale-105 flex items-center justify-center space-x-3 text-lg animate-bounce-once delay-200"
              >
                <i className="fab fa-amazon text-2xl"></i>
                <span>Get on Amazon Appstore</span>
              </a>
            </div>
          </div>
        </section>

        {/* Why Play Our Games Section - New and more engaging */}
        <section className="mt-20 text-center bg-white p-10 rounded-3xl shadow-xl border-t-4 border-blue-500">
          <h2 className="text-4xl font-bold text-gray-800 mb-10 font-inter">
            Why Play Our Games?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="flex flex-col items-center p-6 rounded-xl bg-blue-50 shadow-md transform hover:scale-105 transition-transform duration-300">
              <i className="fas fa-gamepad text-6xl text-blue-600 mb-4 animate-float"></i>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3 font-inter">
                Innovative Gameplay
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Experience fresh mechanics and unique challenges that keep you
                hooked for hours.
              </p>
            </div>
            <div className="flex flex-col items-center p-6 rounded-xl bg-teal-50 shadow-md transform hover:scale-105 transition-transform duration-300">
              <i className="fas fa-brush text-6xl text-teal-600 mb-4 animate-float delay-100"></i>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3 font-inter">
                Stunning Visuals
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Immerse yourself in beautifully crafted worlds with vibrant
                graphics and captivating designs.
              </p>
            </div>
            <div className="flex flex-col items-center p-6 rounded-xl bg-amber-50 shadow-md transform hover:scale-105 transition-transform duration-300">
              <i className="fas fa-users text-6xl text-amber-600 mb-4 animate-float delay-200"></i>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3 font-inter">
                Community Focused
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Join a growing community, share feedback, and influence future
                game developments.
              </p>
            </div>
          </div>
        </section>

        {/* Game Idea Generator Section */}
        <section className="mt-20 text-center bg-gradient-to-r from-blue-700 to-teal-500 text-white p-10 rounded-3xl shadow-xl border-t-4 border-amber-300 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "url('https://placehold.co/1200x800/ffffff/FFC107?text=Abstract+Grid')",
              backgroundSize: "cover",
              filter: "blur(2px)",
            }}
          ></div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 font-inter drop-shadow-lg">
              Feeling Creative? ✨ Generate a Game Idea!
            </h2>
            <p className="text-xl max-w-3xl mx-auto mb-8 font-light">
              Click the button below to get an instant, unique mobile game
              concept from our AI!
            </p>
            <button
              onClick={generateGameIdea}
              disabled={isLoadingIdea}
              className="bg-amber-400 hover:bg-amber-500 text-gray-900 font-bold py-4 px-10 rounded-full shadow-lg transition duration-300 transform hover:scale-105 flex items-center justify-center space-x-3 text-xl mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingIdea ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-magic"></i>
                  <span>Generate New Idea</span>
                </>
              )}
            </button>

            {gameIdea && (
              <div className="mt-10 bg-white bg-opacity-90 text-gray-900 p-8 rounded-2xl shadow-xl border border-amber-300 animate-fade-in-up text-left">
                <h3 className="text-2xl font-bold mb-4 font-inter text-blue-700">
                  Your Fresh Game Idea:
                </h3>
                <p className="text-lg leading-relaxed whitespace-pre-line">
                  {gameIdea}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Featured Games Section - Enhanced visual appeal */}
        <section className="mt-20 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-10 font-inter">
            Our Featured Games
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {/* Game Card 1 */}
            <div className="bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center transition-all duration-300 hover:scale-105 hover:shadow-2xl border-b-4 border-blue-500 hover:border-amber-400">
              <img
                src="https://placehold.co/180x180/00BCD4/FFFFFF?text=Game+1+Icon"
                alt="Game 1 Icon"
                className="w-36 h-36 rounded-3xl mb-6 shadow-lg border-2 border-blue-200"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-3 font-inter">
                Coin Miner Tycoon
              </h3>
              <p className="text-gray-700 text-center mb-6 leading-relaxed">
                Dig for crypto, build your empire, and become the ultimate miner
                tycoon in this addictive simulation!
              </p>
              <div className="flex space-x-4 text-3xl">
                <a
                  href="https://play.google.com/store/apps/details?id=com.insperitygames.bit.coin"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 transform hover:scale-110 transition-transform duration-200"
                >
                  <i className="fab fa-google-play"></i>
                </a>
                <a
                  href="https://www.apple.com/ios/app-store/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-gray-800 transform hover:scale-110 transition-transform duration-200"
                >
                  <i className="fab fa-apple"></i>
                </a>
              </div>
            </div>
            {/* Game Card 2 */}
            <div className="bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center transition-all duration-300 hover:scale-105 hover:shadow-2xl border-b-4 border-teal-500 hover:border-amber-400">
              <img
                src="https://placehold.co/180x180/4CAF50/FFFFFF?text=Game+2+Icon"
                alt="Game 2 Icon"
                className="w-36 h-36 rounded-3xl mb-6 shadow-lg border-2 border-teal-200"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-3 font-inter">
                Strategy Showdown
              </h3>
              <p className="text-gray-700 text-center mb-6 leading-relaxed">
                Master tactical battles and outwit your opponents in this epic
                strategy game with stunning visuals.
              </p>
              <div className="flex space-x-4 text-3xl">
                <a
                  href="#"
                  className="text-blue-600 hover:text-blue-800 transform hover:scale-110 transition-transform duration-200"
                >
                  <i className="fab fa-google-play"></i>
                </a>
                <a
                  href="#"
                  className="text-gray-600 hover:text-gray-800 transform hover:scale-110 transition-transform duration-200"
                >
                  <i className="fab fa-apple"></i>
                </a>
                <a
                  href="#"
                  className="text-orange-600 hover:text-orange-800 transform hover:scale-110 transition-transform duration-200"
                >
                  <i className="fab fa-amazon"></i>
                </a>
              </div>
            </div>
            {/* Game Card 3 */}
            <div className="bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center transition-all duration-300 hover:scale-105 hover:shadow-2xl border-b-4 border-amber-500 hover:border-amber-400">
              <img
                src="https://placehold.co/180x180/FFC107/FFFFFF?text=Game+3+Icon"
                alt="Game 3 Icon"
                className="w-36 h-36 rounded-3xl mb-6 shadow-lg border-2 border-amber-200"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-3 font-inter">
                Puzzle Quest Mania
              </h3>
              <p className="text-gray-700 text-center mb-6 leading-relaxed">
                Challenge your mind with hundreds of unique and brain-teasing
                puzzles that unlock new adventures!
              </p>
              <div className="flex space-x-4 text-3xl">
                <a
                  href="#"
                  className="text-blue-600 hover:text-blue-800 transform hover:scale-110 transition-transform duration-200"
                >
                  <i className="fab fa-google-play"></i>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HomePage;
